import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BellRing, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg, http } from "@/lib/api";

export const AlertsHubToggle = ({ active, onClick }) => (
  <button type="button" onClick={onClick} data-testid="alerts-hub-toggle"
    className={active
      ? "inline-flex items-center gap-2 rounded-full border border-ph-line bg-ph-surface px-5 py-3 text-sm font-semibold text-ph-ink hover:bg-ph-raised"
      : "group inline-flex items-center gap-2.5 rounded-full bg-ph-lime py-2 pl-2 pr-5 text-sm font-semibold text-ph-navy shadow-[0_14px_30px_-12px_rgba(182,255,92,0.6)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(182,255,92,0.5)]"}>
    {active ? <><X className="h-4 w-4" /> Exit alerts mode</> : (
      <><span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#101625]/10"><BellRing className="h-4 w-4 group-hover:animate-[ph-ring_0.8s_ease-in-out]" /></span>Follow multiple brands</>
    )}
  </button>
);

export function AlertsHubBar({ selectedBrands, onClear, onSelectAll, onDone }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const count = selectedBrands.length;

  const submit = async (e) => {
    e.preventDefault();
    if (!count) { toast.error("Tap the brands you want to follow first"); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/alerts/subscribe-many", { email, brand_ids: selectedBrands.map((b) => b.id), consent });
      const pending = data?.pending ?? [];
      toast.success(pending.length ? "Check your inbox to confirm" : "You already follow these brands", { description: pending.length ? `We emailed ${email}. Alerts for ${pending.slice(0, 4).join(", ")}${pending.length > 4 ? "…" : ""} start after you tap the confirm link.` : undefined });
      setEmail("");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pointer-events-none fixed inset-x-3 bottom-3 z-50 flex justify-center">
    <motion.div initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 28 }}
      className="pointer-events-auto w-full md:w-[min(920px,94vw)]" data-testid="alerts-hub-bar">
      <form onSubmit={submit} className="flex flex-col gap-3 rounded-[1.75rem] border border-white/10 bg-[#101625]/95 p-3 text-white shadow-[0_30px_80px_-20px_rgba(11,25,44,0.7)] backdrop-blur-xl md:flex-row md:items-center md:rounded-full md:p-2 md:pl-3">
        <div className="flex items-center gap-3 md:pl-1">
          <div className="flex -space-x-2">
            <AnimatePresence initial={false}>
              {selectedBrands.slice(0, 4).map((b) => (
                <motion.span key={b.id} initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                  className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#101625] bg-gradient-to-br from-[#7388FF] to-[#222D42] font-display text-[10px] font-extrabold">
                  {b.name.slice(0, 2)}
                </motion.span>
              ))}
            </AnimatePresence>
            {count === 0 && <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-dashed border-white/30"><BellRing className="h-4 w-4 text-white/60" /></span>}
          </div>
          <p className="whitespace-nowrap text-sm" data-testid="alerts-hub-count">
            <span className="font-display text-xl font-extrabold">{count}</span> <span className="text-slate-300">selected</span>
          </p>
          <div className="ml-auto flex gap-1 md:ml-2">
            <button type="button" onClick={onSelectAll} data-testid="alerts-hub-select-all" className="rounded-full px-3 py-1.5 text-xs font-semibold text-[#B6FF5C] hover:bg-white/10">All shown</button>
            {count > 0 && <button type="button" onClick={onClear} data-testid="alerts-hub-clear" className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/10">Clear</button>}
          </div>
        </div>
        <div className="flex flex-1 gap-2">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="alerts-hub-email"
            className="h-12 min-w-0 flex-1 rounded-full border border-white/15 bg-white/10 px-5 text-sm text-white outline-none placeholder:text-slate-400 focus:border-[#B6FF5C]" />
          <button type="submit" disabled={busy || !count} data-testid="alerts-hub-submit"
            className="flex h-12 shrink-0 items-center gap-2 rounded-full bg-[#B6FF5C] px-5 text-sm font-semibold text-[#101625] transition-shadow hover:shadow-[0_0_24px_rgba(182,255,92,0.5)] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
            <span className="hidden sm:inline">Follow {count || ""}</span><span className="sm:hidden">Follow</span>
          </button>
        </div>
        <label className="flex items-center gap-2 px-2 text-[11px] leading-snug text-slate-300 md:max-w-[190px]">
          <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="alerts-hub-consent" className="h-4 w-4 shrink-0 accent-[#6366F1]" />
          I agree to receive alert emails. Unsubscribe any time.
        </label>
      </form>
    </motion.div>
    </div>
  );
}
